/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode, lazy, Suspense, useState, use } from 'react';

// Third-party
import { CircleHelp, ShoppingCart, X } from 'lucide-react';

// Commerce SDK
import type { ShopperBasketsV2, ShopperProducts, ShopperPromotions } from '@/scapi';

// Context
import { OrderSummaryContext, useOrderSummaryContext, type OrderSummaryBasket } from './context';
import { ShippingPromoLine } from './shipping-promo-line';
import { OrderDiscountLines } from './order-discount-lines';
import { ProtectionPlanTotalLine } from './protection-plan-total-line';
import { OrderSummaryTaxHelpCopy } from './tax-help-copy';
export type { OrderSummaryBasket } from './context';

// Components
import { Link } from '@/components/link';
import { Typography } from '@/components/typography';
import { Button, buttonVariants } from '@/components/buttons';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/accordion';
import ProductItemsList from '@/components/product-items-list';
import PromoCodeForm from '@/components/promo-code-form';
import { VisaIcon, MastercardIcon, AmexIcon, DiscoverIcon } from '@/components/icons';
import { ExpressPaymentsSkeleton, MyCartSkeleton } from '@/components/checkout/components/checkout-skeletons';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

// Utils
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/currency';
import { useTranslation } from 'react-i18next';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { useNavigate } from '@/hooks/use-navigate';
import { UITarget } from '@/targets/ui-target';
import { getMerchandiseProductItems, getProtectionPlanTotal, isProtectionPlanItem } from '@/lib/extend-basket-utils';
import { getBasketItemCount, getKitGroups, type KitBasketFields } from '@/lib/kit-utils';
import { isCheckoutBlockedCartItem } from '@/lib/cart/unavailable-item';
import { bucketItemsByDeliveryMethod } from '@/lib/checkout/delivery-group-display';

const LazyCartExpressPayments = lazy(() => import('@/components/cart/cart-express-payments'));
const LazyMyCart = lazy(() => import('@/components/my-cart'));
// INT-02 — pre-checkout Extend confirmation modal. Lazy-loaded so the cart
// page initial bundle stays slim — the modal only mounts when the shopper
// has at least one warranty PLI in their basket AND clicks the checkout
// button, matching the project's "Lazy-load overlays" rule.
const LazyExtendCheckoutConfirmationModal = lazy(() =>
    import('@/components/cart/extend-checkout-confirmation-modal').then((m) => ({
        default: m.ExtendCheckoutConfirmationModal,
    }))
);

function getStateRecyclingFeeTotal(basket: OrderSummaryBasket): number | undefined {
    const candidateKeys = ['c_stateRecyclingFee', 'c_recyclingFee', 'c_mattressRecyclingFee'] as const;
    for (const key of candidateKeys) {
        const value = (basket as Record<string, unknown>)[key];
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
            return value;
        }
    }

    const lineItemFeeTotal = (basket.productItems ?? []).reduce((sum, item) => {
        const customValue = (item as Record<string, unknown>).c_stateRecyclingFee;
        if (typeof customValue === 'number' && Number.isFinite(customValue) && customValue > 0) {
            return sum + customValue;
        }
        const adjustmentTotal = (item.priceAdjustments ?? []).reduce((adjSum, adjustment) => {
            const amount = adjustment.price;
            if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) return adjSum;
            const itemText = typeof adjustment.itemText === 'string' ? adjustment.itemText : '';
            const calloutMsg = typeof adjustment.calloutMsg === 'string' ? adjustment.calloutMsg : '';
            const label = `${itemText} ${calloutMsg}`.toLowerCase();
            return label.includes('recycl') ? adjSum + amount : adjSum;
        }, 0);
        return sum + adjustmentTotal;
    }, 0);

    return lineItemFeeTotal > 0 ? lineItemFeeTotal : undefined;
}
// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

/**
 * OrderSummary root component.
 *
 * Computes all basket-derived values once and provides them to sub-components
 * via context. Does NOT render a Card — callers own the shell/layout.
 *
 * Compose with sub-components:
 *   <OrderSummary basket={basket} isEstimate>
 *     <OrderSummary.Heading />
 *     <OrderSummary.Items productsByItemId={...} />
 *     <OrderSummary.PromoCodeForm />
 *     <OrderSummary.Subtotal />
 *     <OrderSummary.ServicesAndFees />
 *     <OrderSummary.PromoCodeForm />  (optional — place between ServicesAndFees and Total)
 *     <OrderSummary.Total />
 *     <OrderSummary.CheckoutAction />
 *     <OrderSummary.ExpressPayments />
 *     <OrderSummary.AcceptedCards />
 *   </OrderSummary>
 */
function OrderSummaryRoot({
    basket,
    isEstimate = false,
    promotions,
    promotionsPromise,
    productsByItemIdForAvailability,
    deliveryZoneStatus,
    shippingTbd = false,
    taxTbd = false,
    tentativePaymentMethodId,
    children,
}: {
    basket: OrderSummaryBasket;
    isEstimate?: boolean;
    /** Resolved promotions map — pass from cart where promotions are already awaited. */
    promotions?: Record<string, ShopperPromotions.schemas['Promotion']>;
    /** Deferred promotions Promise — pass from checkout where the map is streamed in.
     *  Component must be rendered inside a <Suspense> boundary when this prop is used. */
    promotionsPromise?: Promise<Record<string, ShopperPromotions.schemas['Promotion']>>;
    /**
     * Optional product map keyed by productId. Used on cart page so checkout
     * disablement can evaluate catalog-level orderability even when the basket
     * payload omits those flags.
     */
    productsByItemIdForAvailability?: Record<string, ShopperProducts.schemas['Product']>;
    /**
     * SSF-373 — active header-widget delivery-zip `zoneStatus`. Only `'unmapped'`
     * disables the cart's "Continue to Checkout" CTA. Read `useDeliveryZip()`
     * from the store-resolution provider on the calling page and pass through.
     * Optional (defaults to undefined = no gate) so non-cart callers (order-
     * detail, stories, checkout summary) render exactly as before.
     */
    deliveryZoneStatus?: 'mapped' | 'unmapped' | 'unknown';
    /**
     * When `true`, the shipping row always displays "TBD" and shipping is
     * excluded from the displayed total. Use on the cart page where the shopper
     * has not yet selected a delivery method or entered a shipping address.
     */
    shippingTbd?: boolean;
    /**
     * When `true`, the tax row always displays "TBD" and tax is excluded from
     * the displayed total. Use on the cart page where tax cannot be calculated
     * until the shopper provides a delivery address.
     */
    taxTbd?: boolean;
    /**
     * Optimistic override for the shopper's currently-selected tender — used
     * on the checkout page to reflect a Payment-radio change instantly, before
     * the payment step is submitted and the basket recalculates on the server.
     *
     * Value is the SCAPI `paymentMethodId` (`'ACIMA_FINANCE'`, `'CREDIT_CARD'`,
     * `'ASHLEY_FINANCE'`, `'PayPal'`) — same string constants the summary reads
     * off `basket.paymentInstruments`. Empty / undefined = fall back to the
     * basket's actual payment instruments (cart, order-detail).
     *
     * When set to `'ACIMA_FINANCE'`, the sales-tax row hides and the displayed
     * total drops to the pre-tax figure — parity with legacy SFRA
     * `ordertotals.isml` which hides tax when Acima is the selected tender
     * because the shopper pays Acima the tax directly at lease e-sign.
     */
    tentativePaymentMethodId?: string;
    children: ReactNode;
}): ReactElement {
    const { t, i18n } = useTranslation('cart');
    const { currency } = useSite();

    // Resolve whichever promotions source is provided. `use()` suspends when a Promise is
    // passed, so this component must be inside a <Suspense> boundary in that case (checkout).
    // For cart, the already-resolved map is passed directly via `promotions`.
    const resolvedPromotions = promotionsPromise ? use(promotionsPromise) : promotions;

    const hasBasketId = 'basketId' in basket && basket.basketId;
    const hasOrderNo = 'orderNo' in basket && basket.orderNo;
    if (!hasBasketId && !hasOrderNo) {
        return <div>{t('summary.noBasketData')}</div>;
    }

    // --- Derived values ---
    // INT-02 — Warranty PLIs live in the basket as accounting-only line
    // items: their cost contributes to `productSubTotal` (because they ARE
    // product line items in SFCC) but the legacy SFRA cart subtracts that
    // cost from the displayed subtotal so it surfaces only in the dedicated
    // "PROTECTION PLAN(S)" row. We mirror that here so what the customer
    // sees as "Subtotal" is the pure merchandise cost.
    //
    // The displayed total is then recomposed from the visible parts —
    // `merchSubtotalDisplay + orderDiscounts + shipping + protectionPlan + tax`
    // — instead of trusting `basket.orderTotal` directly. Two reasons:
    //   1. Faithfulness to legacy: legacy's ordertotals.isml also builds the
    //      total from the parts shown to the customer, so what they add up
    //      always matches what they see at the bottom of the summary.
    //   2. Defensiveness: with the current Extend reconcile path
    //      (warranty PLI = `priceValue=0` + `createPriceAdjustment(+price)`),
    //      SFCC categorises the positive adjustment as a promotion-style
    //      discount on `productTotal`, so `orderTotal = productTotal +
    //      shipping + tax` ends up short by exactly the warranty cost. A
    //      proper backend fix (`ExtendReconciliation` + `CalculateBasket`)
    //      will follow; until then this keeps the displayed math
    //      self-consistent without papering over the discrepancy in any
    //      hidden way (the cartridge log + the protection-plan line still
    //      surface the warranty cost prominently).
    const protectionPlanTotal = getProtectionPlanTotal(basket);
    const productSubTotalRaw = typeof basket.productSubTotal === 'number' ? basket.productSubTotal : 0;
    const merchandiseSubtotalDisplay = Math.max(0, productSubTotalRaw - protectionPlanTotal);

    const merchandiseTotal =
        typeof basket.productTotal === 'number'
            ? basket.productTotal
            : typeof basket.productSubTotal === 'number'
              ? basket.productSubTotal
              : null;
    const shippingTotalForZeroPay = typeof basket.shippingTotal === 'number' ? basket.shippingTotal : null;
    const isZeroPay = merchandiseTotal === 0 && (shippingTotalForZeroPay === null || shippingTotalForZeroPay === 0);

    const shippingItem = basket.shippingItems?.[0];
    const hasShippingPromos = (shippingItem?.priceAdjustments?.length ?? 0) > 0;
    const shippingPromotionAdjustments = (shippingItem?.priceAdjustments ?? []).map((adj) => ({
        priceAdjustmentId: adj.priceAdjustmentId,
        itemText: adj.itemText,
        // Prefer the calloutMsg from the Promotions API (fetched separately) since the basket
        // response does not echo it back onto price adjustment objects. Fall back to the basket
        // field for forward-compatibility in case a future SFCC version does include it.
        calloutMsg:
            (adj.promotionId ? resolvedPromotions?.[adj.promotionId]?.calloutMsg : undefined) ??
            (typeof adj.calloutMsg === 'string' ? adj.calloutMsg : undefined),
        price: adj.price,
    }));

    const taxStatus = (basket as { c_taxStatus?: string }).c_taxStatus;
    // taxTbd=true (cart page) forces TBD regardless of the basket's taxTotal /
    // c_taxStatus — the shopper hasn't provided a delivery address yet.
    const taxPending = taxTbd || taxStatus === 'pending' || typeof basket.taxTotal !== 'number' || basket.taxTotal < 0;

    // Sum of order-level price adjustments (negative for discounts, positive
    // for surcharges). Read from the basket so promo-code / order-tier
    // discounts already shown in the OrderDiscountLines block are also
    // reflected in the displayed total without double-counting.
    const orderAdjustmentTotal = (basket.orderPriceAdjustments ?? []).reduce(
        (sum, adj) => sum + (typeof adj.price === 'number' ? adj.price : 0),
        0
    );

    // When shippingTbd is true (cart page, no address yet), treat shipping as
    // zero so it is excluded from the displayed total — the label row shows
    // "TBD" via ServicesAndFees independently of this value.
    const shippingTotal = shippingTbd ? 0 : typeof basket.shippingTotal === 'number' ? basket.shippingTotal : 0;
    const taxTotal = typeof basket.taxTotal === 'number' && basket.taxTotal > 0 ? basket.taxTotal : 0;

    // SSF-374 (row-split follow-up) — legacy `ordertotals.isml` renders THREE
    // distinct rows in the cart summary sourced from the JSON blob on
    // `shippingLineItem.custom.priceDetails` — HD ("Delivery"), DS
    // ("Shipping"), and the 3PBB flat White Glove fee — plus a separate
    // shipping-discount row from any shipping-level price adjustments. SFN
    // parity port here parses the same JSON so all three rows read from the
    // ONE authoritative aggregate (`basket.shippingTotal`) with promo
    // discounts still rendered on their own row via `ShippingPromoLine`.
    //
    // Sourcing precedence:
    //   HD  → `c_priceDetails.HD.netPrice` (fallback 0)
    //   DS  → `c_priceDetails.DS.netPrice` (fallback 0)
    //   WG  → `basket.c_whiteGloveTotal` (basket enrichment convenience alias),
    //         falls back to `c_priceDetails.WHITEGLOVE.netPrice` for placed
    //         orders that skip the basket-response hook.
    //
    // Amounts are all pre-shipping-discount (as legacy persists), so the sum
    // (HD + DS + WG − shipping-promo) equals `basket.shippingTotal` and the
    // recomposed `displayTotal` below stays byte-identical.
    //
    // `shippingTbd=true` (cart page, no ZIP/address yet) forces all three
    // amounts to 0 so the rows show "TBD" via the display logic, and none of
    // them contribute to the displayed total.
    const basketRecord = basket as unknown as Record<string, unknown>;

    // Parse `c_priceDetails` once. Corrupt JSON degrades to an empty object
    // so each bucket reads as 0 and the shipping section becomes empty —
    // that's the same "aggregate row" fallback path we had pre-split.
    let priceDetailsParsed: Record<string, { netPrice?: unknown } | undefined> = {};
    const shippingItemPriceDetailsRaw = basket.shippingItems?.[0]?.c_priceDetails;
    if (typeof shippingItemPriceDetailsRaw === 'string' && shippingItemPriceDetailsRaw.length > 0) {
        try {
            priceDetailsParsed = JSON.parse(shippingItemPriceDetailsRaw) as Record<
                string,
                { netPrice?: unknown } | undefined
            >;
        } catch {
            priceDetailsParsed = {};
        }
    }
    const readBucketNet = (key: string): number => {
        const bucket = priceDetailsParsed[key];
        const value = bucket?.netPrice;
        return typeof value === 'number' && value > 0 ? value : 0;
    };
    const rawHdTotal = readBucketNet('HD');
    const rawDsTotal = readBucketNet('DS');
    const rawWhiteGloveFromPriceDetails = readBucketNet('WHITEGLOVE');
    const rawWhiteGloveFromBasket =
        typeof basketRecord.c_whiteGloveTotal === 'number' && basketRecord.c_whiteGloveTotal > 0
            ? basketRecord.c_whiteGloveTotal
            : 0;
    const rawWhiteGloveTotal = rawWhiteGloveFromBasket > 0 ? rawWhiteGloveFromBasket : rawWhiteGloveFromPriceDetails;

    const hdTotal = shippingTbd ? 0 : rawHdTotal;
    const dsTotal = shippingTbd ? 0 : rawDsTotal;
    const whiteGloveTotal = shippingTbd ? 0 : rawWhiteGloveTotal;

    // Item-presence flags — driven by `c_deliveryMethod` on each product item
    // (populated by `OrderDeliveryMethodHelper` regardless of ZIP resolution),
    // so the design's TBD-state rows can render before any fee is computed.
    // Using `bucketItemsByDeliveryMethod` keeps the predicate in lock-step
    // with the checkout delivery-groups card — one source of truth for what
    // counts as HD / DS / DS3PL.
    const productItems = 'productItems' in basket ? (basket.productItems ?? []) : [];
    const deliveryBuckets = bucketItemsByDeliveryMethod(productItems);
    const hasHdItems = deliveryBuckets.buckets.HD.length > 0;
    const hasDsItems = deliveryBuckets.buckets.DS.length > 0;
    const hasDs3plItems = deliveryBuckets.buckets.DS3PL.length > 0;

    // Acima parity — legacy SFRA `ordertotals.isml` hides the sales-tax row and drops the
    // displayed total to the pre-tax figure when Acima is the selected tender, because the
    // shopper pays Acima the sales tax directly at lease e-sign (the ACIMA_FINANCE lease
    // finances `gross − tax`). Two signals contribute:
    //   1. Optimistic override (`tentativePaymentMethodId`) — the shopper just clicked the
    //      Acima radio on the checkout page but the payment step has NOT been submitted
    //      yet, so the basket's payment instruments are stale. Reflect the new tender
    //      immediately for UX consistency (the alternative is the tax row lingering until
    //      the shopper hits "Continue to review", which is confusing).
    //   2. Basket-derived — the ACIMA_FINANCE instrument is already attached (post-submit,
    //      order-detail, or a reload with Acima still on the basket).
    // When `tentativePaymentMethodId` is provided AND non-empty, it takes precedence
    // (the shopper is actively driving the radio) — otherwise fall back to the basket.
    // This prevents "switched to Credit Card after Acima was attached" from still hiding
    // the tax row.
    const basketHasAcimaInstrument = Boolean(
        basket.paymentInstruments?.some((pi) => pi?.paymentMethodId === 'ACIMA_FINANCE')
    );
    const hasAcimaPayment = tentativePaymentMethodId
        ? tentativePaymentMethodId === 'ACIMA_FINANCE'
        : basketHasAcimaInstrument;

    const recomposedTotal =
        merchandiseSubtotalDisplay + orderAdjustmentTotal + shippingTotal + protectionPlanTotal + taxTotal;

    // When tax is pending (no ship-to ZIP yet, SFCC applied a default rate),
    // strip the platform-applied tax from the displayed total so the
    // customer doesn't see a phantom tax line that doesn't yet apply.
    // Same treatment when Acima is (actually or tentatively) the selected tender: the tax
    // row is hidden below, so keep the total consistent with the visible line items.
    const displayTotal = taxPending || hasAcimaPayment ? recomposedTotal - taxTotal : recomposedTotal;

    return (
        <OrderSummaryContext.Provider
            value={{
                basket,
                isEstimate,
                i18nLanguage: i18n.language,
                currency,
                hasShippingPromos,
                shippingPromotionAdjustments,
                isZeroPay,
                taxPending,
                hasAcimaPayment,
                hdTotal,
                dsTotal,
                whiteGloveTotal,
                hasHdItems,
                hasDsItems,
                hasDs3plItems,
                displayTotal,
                merchandiseSubtotalDisplay,
                promotions: resolvedPromotions ?? {},
                productsByItemIdForAvailability,
                deliveryZoneStatus,
                shippingTbd,
            }}>
            <div
                data-testid="sf-order-summary"
                role="region"
                aria-label={t('summary.orderSummary')}
                className="space-y-4">
                {children}
            </div>
        </OrderSummaryContext.Provider>
    );
}

// ---------------------------------------------------------------------------
// OrderSummary.Heading
// ---------------------------------------------------------------------------

function OrderSummaryHeading(): ReactElement {
    const { t } = useTranslation('cart');
    return (
        <>
            <UITarget targetId="sfcc.checkout.myCart.header.before" />
            <Typography
                variant="h3"
                as="h2"
                id="order-summary-heading-desktop"
                className="font-serif text-2xl font-normal leading-9 text-foreground">
                {t('summary.orderSummary')}
            </Typography>
        </>
    );
}

// ---------------------------------------------------------------------------
// OrderSummary.Items
// ---------------------------------------------------------------------------

interface OrderSummaryItemsProps {
    productsByItemId?: Record<string, ShopperProducts.schemas['Product']>;
    expanded?: boolean;
    onEditCart?: () => void;
    onSelectBonusProducts?: () => void;
}

function OrderSummaryItems({
    productsByItemId = {},
    expanded = false,
    onEditCart,
    onSelectBonusProducts,
}: OrderSummaryItemsProps): ReactElement {
    const { t } = useTranslation('cart');
    const { basket } = useOrderSummaryContext();
    // INT-02 — Hide warranty PLIs from the checkout / order-summary item
    // review for parity with the cart list (legacy SFRA cart-list-items.isml).
    // The warranty cost remains visible in the PROTECTION PLAN(S) summary row.
    const merchandiseItems = getMerchandiseProductItems(basket?.productItems);
    // SSF-390 Issues 1 & 8 — count kits by their instance count (each kit is
    // one "item" to the shopper), not by their exploded component PLIs. Uses
    // the shared helper so this row can't drift from the cart page title,
    // mini-cart header, or cookie snapshot badge.
    const totalItems = getBasketItemCount(merchandiseItems, getKitGroups(basket as KitBasketFields | undefined));

    return (
        <Accordion type="single" collapsible className="w-full" defaultValue={expanded ? 'cart-items' : undefined}>
            <AccordionItem value="cart-items">
                <AccordionTrigger className="text-left">
                    <span className="flex-1 text-left text-sm text-primary">
                        <ShoppingCart className="inline mr-2 w-4 h-4" />
                        {t('items.itemsInCart', { count: totalItems })}
                    </span>
                </AccordionTrigger>
                <AccordionContent className="px-0 py-4">
                    <div className="space-y-5">
                        <ProductItemsList
                            productItems={merchandiseItems}
                            productsByItemId={productsByItemId}
                            variant="summary"
                            basket={basket as ShopperBasketsV2.schemas['Basket']}
                            onSelectBonusProducts={onSelectBonusProducts}
                        />
                        <Link
                            to="/cart"
                            onClick={onEditCart}
                            className={buttonVariants({ variant: 'link', className: 'w-full justify-center' })}>
                            {t('items.editCart')}
                        </Link>
                    </div>
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
}

// ---------------------------------------------------------------------------
// OrderSummary.PromoCodeForm
// ---------------------------------------------------------------------------

interface OrderSummaryPromoCodeFormProps {
    borderPlacement?: 'top' | 'bottom';
}

function OrderSummaryPromoCodeForm({ borderPlacement = 'top' }: OrderSummaryPromoCodeFormProps): ReactElement {
    const { basket } = useOrderSummaryContext();
    return (
        <div className={cn('mx-8 border-input', borderPlacement === 'bottom' ? 'border-b mb-3' : 'border-t')}>
            <UITarget targetId="sfcc.orderSummary.promoCode.before" />
            <UITarget targetId="sfcc.orderSummary.promoCode">
                <PromoCodeForm basket={basket} />
            </UITarget>
            <UITarget targetId="sfcc.orderSummary.promoCode.after" />
        </div>
    );
}

// ---------------------------------------------------------------------------
// OrderSummary.Subtotal
// ---------------------------------------------------------------------------

function OrderSummarySubtotal(): ReactElement {
    const { t } = useTranslation('cart');
    const { basket, i18nLanguage, currency, merchandiseSubtotalDisplay } = useOrderSummaryContext();
    const merchandiseItems = getMerchandiseProductItems(basket?.productItems);
    // SSF-390 Issues 1 & 8 — subtotal label must count each kit as its instance
    // count, not the exploded component PLI quantity. Screenshot bug: a 3-piece
    // sectional at kit qty 3 was showing "Subtotal (9 items)" while the cart
    // header + title correctly showed 3. Same helper as the cart title +
    // snapshot ensures every surface reads the same number.
    const totalItems = getBasketItemCount(merchandiseItems, getKitGroups(basket as KitBasketFields | undefined));

    return (
        <div className="space-y-0 text-sm leading-7">
            <div className="px-8">
                <UITarget targetId="sfcc.orderSummary.subtotal.before" />
                <UITarget targetId="sfcc.orderSummary.subtotal">
                    <div className="flex items-center justify-between">
                        <span>{t('summary.subtotalWithCount', { count: totalItems })}</span>
                        <span>{formatCurrency(merchandiseSubtotalDisplay, i18nLanguage, currency)}</span>
                    </div>
                </UITarget>
                <UITarget targetId="sfcc.orderSummary.subtotal.after" />
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// OrderSummary.ServicesAndFees
// ---------------------------------------------------------------------------

function OrderSummaryServicesAndFees(): ReactElement {
    const { t } = useTranslation('cart');
    // `shipping.method.doorstep` (the same "Doorstep Delivery" string the
    // checkout radio and confirmation delivery-method summary use) lives in
    // the `checkout` namespace, hence the second scoped hook. Prefer reusing
    // over introducing a duplicate cart-namespace copy so the three surfaces
    // never drift.
    const { t: tCheckout } = useTranslation('checkout');
    const {
        basket,
        i18nLanguage,
        currency,
        hasShippingPromos,
        shippingPromotionAdjustments,
        isZeroPay,
        taxPending,
        hasAcimaPayment,
        hdTotal,
        dsTotal,
        whiteGloveTotal,
        hasHdItems,
        hasDsItems,
        hasDs3plItems,
        shippingTbd,
    } = useOrderSummaryContext();
    const stateRecyclingFeeTotal = getStateRecyclingFeeTotal(basket);
    const showTaxRow = basket.taxation !== 'gross' && !hasAcimaPayment;
    const showTaxesAndFeesSection = Boolean(stateRecyclingFeeTotal) || showTaxRow;
    const [stateRecyclingFeeOpen, setStateRecyclingFeeOpen] = useState(false);

    // SSF-374 (row-split follow-up) — three delivery-fee rows under one
    // "Shipping" section header (legacy `ordertotals.isml` parity):
    //   • HD      → "White Glove (Ashley)"
    //   • DS      → "Shipping"
    //   • DS3PL   → "White Glove (3rd Party)"  (the 3PBB flat fee)
    //
    // Visibility policy:
    //   • Row RENDERS when the basket has an item of that method
    //     (`c_deliveryMethod` populated regardless of ZIP), so the TBD
    //     state (cart page, no address yet) can show all applicable rows
    //     matching the design mockup.
    //   • Post-ZIP, if the actual bucket amount is 0 the row displays FREE
    //     (matches the pre-split "shippingTotal === 0" behaviour). DS3PL
    //     baskets whose items fail the strict 3PBB predicate correctly land
    //     here — the row shows the DS3PL label with FREE.
    //
    // Amounts are the PRE-shipping-discount netPrice from `c_priceDetails`,
    // so any promo on the shipping line still renders as its own row via
    // `ShippingPromoLine` below and the sum of visible rows continues to
    // equal `basket.shippingTotal`. No double-counting; total unchanged.
    //
    // Aggregate fallback: if the basket has no `c_deliveryMethod` items
    // (extension buckets, malformed enrichment) we can't attribute a
    // presence flag to any row — in that case fall back to the single
    // "Shipping & Handling" row driven off `basket.shippingTotal` so we
    // never silently drop the shipping figure.
    const renderRowAmount = (amount: number): ReactElement => {
        if (shippingTbd) return <span>{t('summary.shippingTbd')}</span>;
        if (amount > 0) return <span>{formatCurrency(amount, i18nLanguage, currency)}</span>;
        return <span className="font-semibold">{t('summary.shippingFree')}</span>;
    };
    // White-glove rows (HD "White Glove (Ashley)" and DS3PL "White Glove
    // (3rd Party)") are NEVER legitimately $0 — Ashley's White Glove is a
    // paid premium service and the 3PBB flat fee is a fixed site pref.
    // A $0 read for either bucket means the fee hasn't been resolved yet
    // (cart page before ZIP is picked → HD ZIP-zone lookup returns null;
    // fresh basket before basket-recalculate runs → priceDetails not
    // written). Rendering "FREE" in those cases is actively misleading
    // ("White Glove is never free" — user report on the initial cart
    // landing screen). Fall back to "TBD" for the two WG rows so the
    // shopper sees "we haven't figured this out yet" rather than a
    // wrong-and-loud "$0". The DS "Shipping" row keeps the FREE
    // rendering because free ground shipping IS a legitimate outcome
    // (Ashley Advantage members, promo waivers, tiered lower-48 base).
    const renderWhiteGloveAmount = (amount: number): ReactElement => {
        if (shippingTbd) return <span>{t('summary.shippingTbd')}</span>;
        if (amount > 0) return <span>{formatCurrency(amount, i18nLanguage, currency)}</span>;
        return <span>{t('summary.shippingTbd')}</span>;
    };
    // HD row label follows the shopper's HD-vs-Doorstep selection so shoppers
    // read what they actually chose:
    //   • `c_hdShippingType = 'THD'` → shopper picked Doorstep Delivery
    //     (basket carries the reduced THD fee via `hdOptions.thdFee`).
    //   • Anything else (including undefined on a fresh basket) → the White
    //     Glove default (`hdOptions.hdFee`), so the row keeps its
    //     "White Glove (Ashley)" mockup label.
    // Reuses `shipping.method.doorstep` — the same string the checkout radio
    // and confirmation delivery-method summary use — so all three surfaces
    // (cart / checkout / confirmation) say "Doorstep Delivery" verbatim.
    const hdShippingType = (basket as { c_hdShippingType?: 'HD' | 'THD' }).c_hdShippingType;
    const hdRowLabel = hdShippingType === 'THD' ? tCheckout('shipping.method.doorstep') : t('summary.whiteGloveAshley');
    const showAnyDeliveryRow = hasHdItems || hasDsItems || hasDs3plItems;
    // Suppress the "Shipping" (ground) row when it would render as FREE
    // AND a white-glove row is already visible (either the HD "White Glove
    // (Ashley)" row or the DS3PL "White Glove (3rd Party)" row). Shoppers
    // parse the aggregate shipping cost from the white-glove row(s) in that
    // case, and a redundant "Shipping FREE" line adds visual noise without
    // information. The suppression is strictly cosmetic — it does not affect
    // basket.shippingTotal or Estimated Total, and the row still renders
    // when the DS cost is non-zero (tiered AK/HI/PR bases) or when shipping
    // is TBD (shopper hasn't committed a ZIP yet, so we must not imply the
    // ground portion is $0). Also still renders when no white-glove row is
    // visible — the row is the ONLY visible shipping row in that case.
    const suppressGroundShippingFreeRow = hasDsItems && !shippingTbd && dsTotal === 0 && (hasHdItems || hasDs3plItems);
    // SSF-374 (UX polish) — collapse the 3-row split into a single "Total
    // TBD" line while the whole section would render as TBD. Three "TBD"
    // rows on cart landing (before ZIP) is visually noisy — the shopper
    // can't act on any of them, and the row labels tell them nothing new
    // (they already see the products in the cart). As soon as ANY row
    // resolves to a real amount (post-ZIP / post-recalculate), the full
    // 3-row split renders. This matches the BA's spec intent ("Pre-delivery
    // calculation: single 'Total TBD' line; Post-delivery: broken out
    // breakdown"). Aggregate fallback (`renderAggregateFallback` below)
    // already renders "TBD" when `shippingTbd` is true, so we just force
    // that path here instead of duplicating the collapse logic.
    const collapseTbdSection = shippingTbd && showAnyDeliveryRow;
    const renderBreakoutRows = showAnyDeliveryRow && !collapseTbdSection;
    const rawShippingAggregate = typeof basket.shippingTotal === 'number' ? basket.shippingTotal : null;
    const renderAggregateFallback = (): ReactElement => {
        if (shippingTbd) return <span>{t('summary.shippingTbd')}</span>;
        if (rawShippingAggregate === 0) return <span className="font-semibold">{t('summary.shippingFree')}</span>;
        if (rawShippingAggregate !== null && rawShippingAggregate > 0) {
            return <span>{formatCurrency(rawShippingAggregate, i18nLanguage, currency)}</span>;
        }
        return <span>{t('summary.shippingTbd')}</span>;
    };

    return (
        <>
            {/* Zero-pay banner */}
            <UITarget targetId="sfcc.orderSummary.zeroPay">
                {isZeroPay ? (
                    <div
                        data-testid="order-summary-zero-pay-banner"
                        role="status"
                        className="rounded-md bg-success/10 px-3 py-2 text-sm font-medium text-success">
                        {t('zeroPay.banner')}
                    </div>
                ) : null}
            </UITarget>

            <div className="space-y-0 pt-3 px-8 text-sm leading-7">
                {/* Order-level price adjustments (CART-13) */}
                <UITarget targetId="sfcc.orderSummary.adjustments.before" />
                <UITarget targetId="sfcc.orderSummary.adjustments">
                    <OrderDiscountLines adjustments={basket.orderPriceAdjustments ?? []} />
                </UITarget>
                <UITarget targetId="sfcc.orderSummary.adjustments.after" />

                {/*
                 * INT-02 — Protection Plan(s) breakdown.
                 *
                 * Driven entirely by the Extend basket enrichment in
                 * `int_sfnext_extend` (`ExtendBasketEnricher` adds
                 * `c_isProtectionPlan`, `c_ppCost`, `c_ppTypeName` per warranty
                 * PLI). The line renders above the Tax row to match the legacy
                 * SFRA cart summary, and self-hides when no warranty PLIs are
                 * present so non-protection-eligible baskets are unaffected.
                 */}
                <UITarget targetId="sfcc.orderSummary.protectionPlans.before" />
                <UITarget targetId="sfcc.orderSummary.protectionPlans">
                    <ProtectionPlanTotalLine />
                </UITarget>
                <UITarget targetId="sfcc.orderSummary.protectionPlans.after" />

                <UITarget targetId="sfcc.orderSummary.giftCards.applied" />

                {/* Shipping — SSF-374 row-split follow-up */}
                <UITarget targetId="sfcc.orderSummary.shipping.before" />
                <UITarget targetId="sfcc.orderSummary.shipping">
                    <div className="border-t border-input py-3">
                        <p className="text-xs font-semibold leading-4 text-foreground">{t('summary.shipping')}</p>
                        {/*
                         * `space-y-1` only when the 3-row split renders — the
                         * aggregate-fallback path keeps the pre-split
                         * `className="mt-1"` byte-identical so existing
                         * Storybook snapshots stay stable.
                         */}
                        <div className={cn('mt-1', renderBreakoutRows && 'space-y-1')}>
                            {hasShippingPromos &&
                                (shippingTbd ? (
                                    shippingPromotionAdjustments.map((adj) => (
                                        <div
                                            key={adj.priceAdjustmentId ?? 'shipping-promo-tbd'}
                                            data-testid="shipping-promo-tbd"
                                            className="flex items-center justify-between">
                                            <span className="text-promo-discount">
                                                {adj.calloutMsg ?? adj.itemText}
                                            </span>
                                            <span className="text-promo-discount">{t('summary.shippingTbd')}</span>
                                        </div>
                                    ))
                                ) : (
                                    <ShippingPromoLine
                                        adjustments={shippingPromotionAdjustments}
                                        shippingTotal={basket.shippingTotal}
                                    />
                                ))}
                            {renderBreakoutRows ? (
                                <>
                                    {hasHdItems ? (
                                        <>
                                            <UITarget targetId="sfcc.orderSummary.shipping.hd.before" />
                                            <UITarget targetId="sfcc.orderSummary.shipping.hd">
                                                <div
                                                    data-testid="order-summary-shipping-hd"
                                                    className="flex items-center justify-between">
                                                    <span>{hdRowLabel}</span>
                                                    {renderWhiteGloveAmount(hdTotal)}
                                                </div>
                                            </UITarget>
                                            <UITarget targetId="sfcc.orderSummary.shipping.hd.after" />
                                        </>
                                    ) : null}
                                    {hasDsItems && !suppressGroundShippingFreeRow ? (
                                        <>
                                            <UITarget targetId="sfcc.orderSummary.shipping.ds.before" />
                                            <UITarget targetId="sfcc.orderSummary.shipping.ds">
                                                <div
                                                    data-testid="order-summary-shipping-ds"
                                                    className="flex items-center justify-between">
                                                    <span>{t('summary.groundShipping')}</span>
                                                    {renderRowAmount(dsTotal)}
                                                </div>
                                            </UITarget>
                                            <UITarget targetId="sfcc.orderSummary.shipping.ds.after" />
                                        </>
                                    ) : null}
                                    {hasDs3plItems ? (
                                        <>
                                            <UITarget targetId="sfcc.orderSummary.shipping.ds3pl.before" />
                                            <UITarget targetId="sfcc.orderSummary.shipping.ds3pl">
                                                <div
                                                    data-testid="order-summary-shipping-ds3pl"
                                                    className="flex items-center justify-between">
                                                    <span>{t('summary.whiteGlove3rdParty')}</span>
                                                    {renderWhiteGloveAmount(whiteGloveTotal)}
                                                </div>
                                            </UITarget>
                                            <UITarget targetId="sfcc.orderSummary.shipping.ds3pl.after" />
                                        </>
                                    ) : null}
                                </>
                            ) : (
                                // Aggregate fallback — either:
                                //   (a) `collapseTbdSection` — the whole
                                //       section would render as TBD, so we
                                //       collapse to a single "Total TBD" line
                                //       (SSF-374 UX polish); or
                                //   (b) `!showAnyDeliveryRow` — basket has no
                                //       items with a known `c_deliveryMethod`
                                //       (extension delivery buckets, malformed
                                //       enrichment), fall back to the legacy
                                //       single-line "Shipping & Handling" row
                                //       so we never silently drop the aggregate
                                //       shipping figure and existing Storybook
                                //       snapshots stay stable.
                                <div
                                    // `data-testid` is added ONLY in the
                                    // collapse case (new visible state) so
                                    // the pre-existing extension-fallback
                                    // path (no c_deliveryMethod on any PLI)
                                    // keeps byte-identical DOM and its
                                    // Storybook snapshots stay stable.
                                    {...(collapseTbdSection
                                        ? { 'data-testid': 'order-summary-shipping-tbd-collapsed' }
                                        : {})}
                                    className="flex items-center justify-between">
                                    <span>{t('summary.shippingLineItem')}</span>
                                    {renderAggregateFallback()}
                                </div>
                            )}
                        </div>
                    </div>
                </UITarget>
                <UITarget targetId="sfcc.orderSummary.shipping.after" />

                {showTaxesAndFeesSection ? (
                    <div className="border-t border-input py-3">
                        <p className="text-xs font-semibold leading-4 text-foreground">{t('summary.taxesAndFees')}</p>
                        <div className="mt-1 space-y-1">
                            {stateRecyclingFeeTotal ? (
                                <UITarget targetId="sfcc.orderSummary.stateRecyclingFee">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1">
                                            <span>{t('summary.stateRecyclingFees')}</span>
                                            <Popover
                                                open={stateRecyclingFeeOpen}
                                                onOpenChange={setStateRecyclingFeeOpen}>
                                                <PopoverTrigger asChild>
                                                    <button
                                                        type="button"
                                                        onMouseEnter={() => setStateRecyclingFeeOpen(true)}
                                                        onMouseLeave={() => setStateRecyclingFeeOpen(false)}
                                                        onFocus={() => setStateRecyclingFeeOpen(true)}
                                                        onBlur={() => setStateRecyclingFeeOpen(false)}
                                                        aria-label={t(
                                                            'summary.stateRecyclingFeeTooltipTriggerAriaLabel'
                                                        )}
                                                        className="inline-flex size-4 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
                                                        data-testid="state-recycling-fee-tooltip-trigger">
                                                        <CircleHelp className="size-3.5" aria-hidden />
                                                    </button>
                                                </PopoverTrigger>
                                                <PopoverContent
                                                    align="start"
                                                    side="top"
                                                    sideOffset={8}
                                                    onMouseEnter={() => setStateRecyclingFeeOpen(true)}
                                                    onMouseLeave={() => setStateRecyclingFeeOpen(false)}
                                                    className="w-[320px] rounded-xs border border-input bg-card p-4 text-foreground shadow-md"
                                                    data-testid="state-recycling-fee-tooltip-content">
                                                    <div className="space-y-3 text-sm leading-5">
                                                        <div className="flex items-start justify-between gap-3">
                                                            <p className="font-semibold">
                                                                {t('summary.stateRecyclingFeeTooltipTitle')}
                                                            </p>
                                                            <button
                                                                type="button"
                                                                onClick={() => setStateRecyclingFeeOpen(false)}
                                                                aria-label={t(
                                                                    'summary.stateRecyclingFeeTooltipCloseAriaLabel'
                                                                )}
                                                                className="text-muted-foreground hover:text-foreground">
                                                                <X className="size-4" aria-hidden />
                                                            </button>
                                                        </div>
                                                        <p className="text-muted-foreground">
                                                            {t('summary.stateRecyclingFeeTooltipBody')}
                                                        </p>
                                                    </div>
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                        {taxPending ? (
                                            <span>{t('summary.taxTbd')}</span>
                                        ) : (
                                            <span>
                                                {formatCurrency(stateRecyclingFeeTotal, i18nLanguage, currency)}
                                            </span>
                                        )}
                                    </div>
                                </UITarget>
                            ) : null}

                            {/*
                             * Tax
                             *
                             * SCAPI taxTotal / orderTotal are always populated (the platform calc chain runs
                             * unconditionally). We read c_taxStatus to know whether tax is real vs. the
                             * platform's default rate applied to an addressless basket:
                             *   "pending"    → no ship-to address yet; render TBD and back the default tax
                             *                  out of the displayed total.
                             *   "calculated" → tax is real; render the amount.
                             * Falls back to the legacy taxTotal-is-null heuristic for Order responses.
                             *
                             * Gross taxation means tax is already included in the displayed
                             * prices, so the summary must not surface a separate tax line
                             * (parity with GA). Net / undefined taxation renders the line.
                             *
                             * Acima: legacy SFRA `ordertotals.isml` hides the sales-tax row when
                             * `pdict.isAcimaPayment` because the shopper pays sales tax directly to Acima
                             * at lease e-sign — the basket's `taxTotal` is zeroed by
                             * `AcimaTaxHelper.removeTaxIfAcima` (see CalculateBasket.js), so rendering
                             * "Tax: $0.00" would mislead the shopper into thinking no tax is owed. Match
                             * legacy behavior by hiding the row entirely whenever the basket carries an
                             * ACIMA_FINANCE payment instrument.
                             */}
                            {showTaxRow && (
                                <>
                                    <UITarget targetId="sfcc.orderSummary.tax.before" />
                                    <UITarget targetId="sfcc.orderSummary.tax">
                                        <UITarget targetId="sfcc.orderSummary.tax.line">
                                            <div className="flex items-center justify-between">
                                                <span>{t('summary.tax')}</span>
                                                {taxPending ? (
                                                    <span>{t('summary.taxTbd')}</span>
                                                ) : (
                                                    <span>
                                                        {formatCurrency(
                                                            basket.taxTotal as number,
                                                            i18nLanguage,
                                                            currency
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        </UITarget>
                                    </UITarget>
                                    <UITarget targetId="sfcc.orderSummary.tax.after" />
                                </>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>
        </>
    );
}

// ---------------------------------------------------------------------------
// OrderSummary.Total
// ---------------------------------------------------------------------------

function OrderSummaryTotal(): ReactElement {
    const { t } = useTranslation('cart');
    const { isEstimate, i18nLanguage, currency, displayTotal } = useOrderSummaryContext();
    return (
        <>
            <div className="px-8 pt-3">
                <UITarget targetId="sfcc.orderSummary.total.before" />
                <UITarget targetId="sfcc.orderSummary.total">
                    <div className="flex items-center justify-between font-bold">
                        <span>{isEstimate ? t('summary.estimatedTotal') : t('summary.total')}</span>
                        <span>{formatCurrency(displayTotal, i18nLanguage, currency)}</span>
                    </div>
                </UITarget>
                <UITarget targetId="sfcc.orderSummary.total.after" />
            </div>
            <UITarget targetId="sfcc.cart.loyalty.pointsEarned" />
            <UITarget targetId="sfcc.cart.giftCards.apply" />
            <UITarget targetId="sfcc.cart.identity.verification" />
        </>
    );
}

// ---------------------------------------------------------------------------
// OrderSummary.CheckoutAction
// ---------------------------------------------------------------------------

/**
 * Renders the cart's "Continue to Checkout" CTA.
 *
 * INT-02 / CART-08 — When the basket contains one or more Extend warranty
 * PLIs (`c_isProtectionPlan === true` on any product item), the click is
 * intercepted to display the pre-checkout "Product Protection by Extend"
 * confirmation modal first. The modal's "continue with selected items"
 * button completes the navigation; "Go back" simply dismisses the modal
 * and the shopper stays on the cart page so they can uncheck individual
 * Extend toggles before re-attempting checkout.
 *
 * Baskets without warranty PLIs continue to navigate directly to
 * `/checkout` via the underlying `<Link>` — no modal ever mounts in that
 * common path, and the lazy-loaded modal chunk is never fetched.
 */
function OrderSummaryCheckoutAction(): ReactElement {
    const { t } = useTranslation('cart');
    const { basket, deliveryZoneStatus } = useOrderSummaryContext();
    const navigate = useNavigate();
    const [extendModalOpen, setExtendModalOpen] = useState(false);
    const basketAtpStatus = (basket as { c_atpStatus?: 'enriched' | 'no_zip' | 'error' | null }).c_atpStatus;
    const hasUnavailableItems = getMerchandiseProductItems(basket.productItems).some((item) => {
        return isCheckoutBlockedCartItem(item, basketAtpStatus);
    });
    // SSF-373 — two independent signals gate Proceed to Checkout for the out-of-region case:
    //
    //   1. `deliveryZoneStatus === 'unmapped'` — the shopper's active header-widget ZIP from
    //      the `ash-delivery-zip` cookie is out of region. This is the primary signal
    //      driven by the shopper's declared delivery intent.
    //   2. `basket.c_shippingUnavailable === true` — cartridge-side belt-and-braces flag
    //      stamped by `ShippingCostHelper.stampShippingUnavailableFlag` when the basket has
    //      HD line items but the effective ZIP has no `hdShippingCost` custom-object record.
    //      Catches shoppers who bypass the header widget entirely (bot, direct SCAPI caller,
    //      saved shipping address restored from customer profile) so the storefront never
    //      lets an OOR checkout proceed regardless of client path.
    //
    // Only `'unmapped'` triggers the widget-side gate — a `'unknown'` lookup failure MUST NOT
    // gate the button (see `ZoneResolutionStatus` docs). The cartridge flag is the definitive
    // server-computed signal and is trusted as-is.
    const basketShippingUnavailable = (basket as { c_shippingUnavailable?: boolean }).c_shippingUnavailable === true;
    const isOutOfRegion = deliveryZoneStatus === 'unmapped' || basketShippingUnavailable;
    const isCheckoutDisabled = hasUnavailableItems || isOutOfRegion;

    // INT-02 — Detect Extend warranty PLIs (`c_isProtectionPlan === true`)
    // in the basket. The cartridge reconciliation only creates a warranty
    // PLI when its parent merchandise PLI is actively selected, so the
    // presence of any warranty PLI is a sufficient trigger for the
    // pre-checkout confirmation modal — no need to also re-check
    // `c_protectionPlanSelected` on the merchandise side.
    const hasExtendProtection = (basket.productItems ?? []).some((item) => isProtectionPlanItem(item));

    const handleCheckoutClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
        if (isCheckoutDisabled) {
            event.preventDefault();
            return;
        }
        if (!hasExtendProtection) return;
        // Intercept the link click and surface the modal first; the modal's
        // "continue with selected items" CTA performs the actual navigation.
        event.preventDefault();
        setExtendModalOpen(true);
    };

    return (
        <>
            <hr className="mx-8 my-4 border-border" />
            <div className="px-8 my-4">
                <Button asChild className="mt-1 h-12 w-full text-sm uppercase tracking-[0.16em]">
                    <Link
                        to="/checkout"
                        data-testid="cart-continue-to-checkout"
                        aria-disabled={isCheckoutDisabled || undefined}
                        aria-haspopup={hasExtendProtection ? 'dialog' : undefined}
                        className={cn(isCheckoutDisabled && 'pointer-events-none opacity-60')}
                        onClick={handleCheckoutClick}>
                        {t('checkout.continueToCheckout')}
                    </Link>
                </Button>
            </div>
            <UITarget targetId="sfcc.cart.payments.expressCheckout" />
            {hasExtendProtection ? (
                <Suspense fallback={null}>
                    <LazyExtendCheckoutConfirmationModal
                        open={extendModalOpen}
                        onOpenChange={setExtendModalOpen}
                        onContinue={() => {
                            setExtendModalOpen(false);
                            // `useNavigate` returns a Promise we don't observe;
                            // explicit void mirrors the project convention.
                            void navigate('/checkout');
                        }}
                    />
                </Suspense>
            ) : null}
        </>
    );
}

// ---------------------------------------------------------------------------
// OrderSummary.ExpressPayments
// ---------------------------------------------------------------------------

function OrderSummaryExpressPayments(): ReactElement {
    return (
        <Suspense fallback={<ExpressPaymentsSkeleton />}>
            <UITarget targetId="checkout.expressPayments.before" />
            <UITarget targetId="checkout.expressPayments">
                <LazyCartExpressPayments />
            </UITarget>
            <UITarget targetId="checkout.expressPayments.after" />
        </Suspense>
    );
}

// ---------------------------------------------------------------------------
// OrderSummary.AcceptedCards
// ---------------------------------------------------------------------------

function OrderSummaryAcceptedCards(): ReactElement {
    return (
        <div className="flex justify-center pt-2">
            <VisaIcon width={40} height={32} className="mr-2" />
            <MastercardIcon width={40} height={32} className="mr-2" />
            <AmexIcon width={40} height={32} className="mr-2" />
            <DiscoverIcon width={40} height={32} className="mr-2" />
        </div>
    );
}

// ---------------------------------------------------------------------------
// OrderSummary.MyCart
// ---------------------------------------------------------------------------

interface OrderSummaryMyCartProps {
    productMapPromise: Promise<Record<string, ShopperProducts.schemas['Product']>>;
    promotionsPromise?: Promise<Record<string, ShopperPromotions.schemas['Promotion']>>;
}

function OrderSummaryMyCartContent({ productMapPromise, promotionsPromise }: OrderSummaryMyCartProps): ReactElement {
    const { basket } = useOrderSummaryContext();
    const productMap = use(productMapPromise);
    const promotions = promotionsPromise ? use(promotionsPromise) : undefined;

    if (!('basketId' in basket)) return <></>;

    return <LazyMyCart basket={basket} productMap={productMap} promotions={promotions} />;
}

function OrderSummaryMyCart({ productMapPromise, promotionsPromise }: OrderSummaryMyCartProps): ReactElement {
    const { basket } = useOrderSummaryContext();
    const itemCount = 'productItems' in basket ? (basket.productItems?.length ?? 2) : 2;

    return (
        <div className="px-8">
            <UITarget targetId="checkout.myCart.before" />
            <UITarget targetId="checkout.myCart">
                <Suspense fallback={<MyCartSkeleton itemCount={itemCount} />}>
                    <OrderSummaryMyCartContent
                        productMapPromise={productMapPromise}
                        promotionsPromise={promotionsPromise}
                    />
                </Suspense>
            </UITarget>
            <UITarget targetId="checkout.myCart.after" />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Attach sub-components and export
// ---------------------------------------------------------------------------

OrderSummaryRoot.Heading = OrderSummaryHeading;
OrderSummaryRoot.Items = OrderSummaryItems;
OrderSummaryRoot.PromoCodeForm = OrderSummaryPromoCodeForm;
OrderSummaryRoot.Subtotal = OrderSummarySubtotal;
OrderSummaryRoot.ServicesAndFees = OrderSummaryServicesAndFees;
OrderSummaryRoot.TaxHelpCopy = OrderSummaryTaxHelpCopy;
OrderSummaryRoot.Total = OrderSummaryTotal;
OrderSummaryRoot.CheckoutAction = OrderSummaryCheckoutAction;
OrderSummaryRoot.ExpressPayments = OrderSummaryExpressPayments;
OrderSummaryRoot.AcceptedCards = OrderSummaryAcceptedCards;
OrderSummaryRoot.MyCart = OrderSummaryMyCart;

export default OrderSummaryRoot;
